'use client';
import {
  OrderRequiredDocumentItem,
  OrderRequiredDocumentVersionItem,
} from 'bff';
import { OrderFileUploader } from 'database';
import { format } from 'date-fns';
import React from 'react';
import { getHumanFileSize } from 'utils';
import {
  IconBuilding,
  IconExclamationCircle,
  IconFileText,
  IconFolderExclamation,
  IconHistory,
  IconTool,
  IconUser,
  IconUserCheck,
  IconUserX,
} from '@tabler/icons-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  TooltipProvider,
  TooltipContent,
  TooltipTrigger,
  Tooltip,
} from 'ui';
import { FileCardIcon } from './FileCard';
import { actorName } from '../utils/actorName';

// order_required_docs — one accordion section per requirement snapshotted into
// the order: name, obligatoriedad and status in the trigger; current file,
// review trace and actions in the body (older versions live in a dialog).
// Several sections can stay open at once.
// The trigger states who owns the upload, phrased against `viewerParty` so the
// reader answers "¿me toca a mí?" without opening the section. Actions are
// injected per app via render props: upload (party-matched) and review
// (insurer, latest only).
type RequiredDocumentDisplayStatus =
  | 'missing'
  | 'in_review'
  | 'uploaded'
  | 'accepted'
  | 'rejected';

const displayStatus = (
  item: OrderRequiredDocumentItem,
): RequiredDocumentDisplayStatus => {
  const [latest] = item.versions;
  if (!latest) return 'missing';
  if (latest.status === 'accepted') return 'accepted';
  if (latest.status === 'rejected') return 'rejected';
  return item.requires_review ? 'in_review' : 'uploaded';
};

const STATUS_BADGE: Record<
  RequiredDocumentDisplayStatus,
  { label: string; variant: 'warning' | 'success' | 'danger' | 'secondary' }
> = {
  missing: { label: 'Pendiente', variant: 'warning' },
  in_review: { label: 'Pendiente de revisión', variant: 'warning' },
  uploaded: { label: 'Cargado', variant: 'success' },
  accepted: { label: 'Aprobado', variant: 'success' },
  rejected: { label: 'Rechazado', variant: 'danger' },
};

// Quién tiene que cargarlo, resuelto contra la parte que está mirando: la
// pregunta que se responde de un vistazo es "¿me toca a mí?".
const UploaderChip = ({
  uploader,
  viewerParty,
}: {
  uploader: OrderFileUploader;
  viewerParty?: OrderFileUploader;
}) => {
  const isMine = uploader === viewerParty;

  const Icon = isMine
    ? IconUser
    : uploader === 'insurer'
    ? IconBuilding
    : IconTool;

  const label = isMine
    ? 'Lo cargás vos'
    : uploader === 'insurer'
    ? 'Lo carga la aseguradora'
    : 'Lo carga el proveedor';

  return (
    <span
      className={cn(
        'flex items-center gap-1 text-xs shrink-0',
        isMine ? 'text-foreground font-medium' : 'text-muted-foreground',
      )}
    >
      <Icon className='ml-[1px] h-3.5 w-3.5 shrink-0 mr-1' />
      {label}
    </span>
  );
};

const formatDate = (date: Date | string) =>
  format(new Date(date), 'dd/MM/yyyy HH:mm');

const VersionFileRow = ({
  version,
}: {
  version: OrderRequiredDocumentVersionItem;
}) => (
  <a
    className='group w-fit font-medium py-1'
    target='_blank'
    rel='noreferrer'
    href={version.file.url}
  >
    <div className='flex items-center'>
      <FileCardIcon className='w-4 h-4' mimetype={version.file.mimetype!} />
      <span className='text-[16px] hover:underline hover:underline-offset-2'>
        {version.file.original_name}
      </span>
      {version.file.size != null && (
        <span className='text-xs text-muted-foreground mt-0.5 ml-1'>
          ({getHumanFileSize(Number(version.file.size), true)})
        </span>
      )}
    </div>
  </a>
);

// Las versiones anteriores son consulta puntual, no parte del flujo de trabajo:
// van a un modal para no alargar la sección con historial que casi nunca se lee.
const VersionHistoryDialog = ({
  documentName,
  versions,
}: {
  documentName: string;
  versions: OrderRequiredDocumentVersionItem[];
}) => (
  <Dialog>
    <DialogTrigger asChild>
      <button
        type='button'
        className='flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground'
      >
        <IconHistory className='h-3.5 w-3.5 shrink-0' />
        Versiones anteriores
      </button>
    </DialogTrigger>
    <DialogContent className='sm:max-w-md'>
      <DialogHeader>
        <DialogTitle>Historial de versiones</DialogTitle>
        <DialogDescription>{documentName}</DialogDescription>
      </DialogHeader>
      <div className='flex flex-col gap-3 max-h-[60vh] overflow-y-auto'>
        {versions.map((version) => (
          <div
            key={version.id}
            className='flex flex-col gap-0.5 rounded-xl border p-3'
          >
            <div className='flex items-center justify-between gap-2'>
              <span className='text-[13px] font-medium'>
                Versión {version.version}
              </span>
              <Badge
                className='w-fit shrink-0'
                variant={
                  version.status === 'accepted'
                    ? 'success'
                    : version.status === 'rejected'
                    ? 'danger'
                    : 'secondary'
                }
              >
                {version.status === 'accepted'
                  ? 'Aprobado'
                  : version.status === 'rejected'
                  ? 'Rechazado'
                  : 'Reemplazado'}
              </Badge>
            </div>
            <VersionFileRow version={version} />
            <span className='text-xs text-muted-foreground'>
              {actorName(version.created_by, 'un usuario')} el{' '}
              {formatDate(version.created_at)}
            </span>
            {version.status === 'rejected' && version.rejected_reason && (
              <span className='text-xs text-muted-foreground whitespace-pre-wrap'>
                Motivo:
                <span className='font-medium text-foreground pl-1'>
                  «{version.rejected_reason}»
                </span>
              </span>
            )}
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

export const OrderRequiredDocumentsList = ({
  items,
  viewerParty,
  renderUploadAction,
  renderReviewActions,
}: {
  items: OrderRequiredDocumentItem[];
  // La parte que está mirando la orden, para poder decir "lo cargás vos".
  viewerParty?: OrderFileUploader;
  renderUploadAction?: (args: {
    item: OrderRequiredDocumentItem;
  }) => React.ReactNode;
  renderReviewActions?: (args: {
    item: OrderRequiredDocumentItem;
    version: OrderRequiredDocumentVersionItem;
  }) => React.ReactNode;
}) => {
  if (items.length === 0)
    return (
      <div className='text-center text-sm text-muted-foreground flex items-center justify-center gap-2 py-4'>
        <IconFolderExclamation className='w-4 h-4' />
        <span>No hay documentación requerida configurada para esta orden.</span>
      </div>
    );

  return (
    <Accordion type='single' collapsible className='rounded-xl border bg-white'>
      {items.map((item) => {
        const [latest, ...previous] = item.versions;
        const status = displayStatus(item);
        const badge = STATUS_BADGE[status];

        return (
          <AccordionItem
            key={item.id}
            value={item.id}
            className='border-b last:border-b-0 overflow-hidden'
          >
            {/* Sin fondo en reposo: gris apenas insinuado al pasar por encima y
                un punto más marcado mientras la sección está abierta. */}
            <AccordionTrigger className='hover:no-underline px-4 rounded-xl hover:bg-gray-50/60 data-[state=open]:bg-gray-50 data-[state=open]:hover:bg-gray-50'>
              <div className='flex flex-col md:flex-row justify-between md:items-center gap-2 w-full pr-3'>
                <div className='flex flex-col gap-0.5 items-start text-left'>
                  <h2 className='font-semibold text-base flex items-center gap-1 flex-wrap'>
                    <IconFileText className='w-4 h-4 shrink-0 mr-0.5' />
                    {item.name}{' '}
                    {item.is_required ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='text-red-700 cursor-default'>
                              *
                            </span>
                            {/* <IconExclamationCircle className='w-4 h-4 shrink-0 mt-0.5 text-red-700' /> */}
                          </TooltipTrigger>
                          <TooltipContent className='font-normal'>
                            Documentación requerida.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className='text-xs text-muted-foreground font-normal mt-0.5'>
                        (opcional)
                      </span>
                    )}
                  </h2>
                  <UploaderChip
                    uploader={item.uploader}
                    viewerParty={viewerParty}
                  />
                  {item.description && (
                    <span className='text-xs text-muted-foreground whitespace-pre-wrap'>
                      {item.description}
                    </span>
                  )}
                </div>
                <Badge className='w-fit shrink-0' variant={badge.variant}>
                  {badge.label}
                </Badge>
              </div>
            </AccordionTrigger>

            <AccordionContent className='px-4 pt-3 pb-2 border-t'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                {latest ? (
                  <div className='flex flex-col gap-1'>
                    <VersionFileRow version={latest} />
                    <span className='text-xs text-muted-foreground'>
                      Versión {latest.version} · Subido por{' '}
                      {actorName(latest.created_by, 'un usuario')} el{' '}
                      {formatDate(latest.created_at)}
                    </span>

                    {latest.status === 'accepted' && (
                      <p className='flex items-center gap-1.5 py-0.5 text-[13px] text-muted-foreground'>
                        <IconUserCheck className='h-3.5 w-3.5 shrink-0' />
                        <span>
                          Aprobado por{' '}
                          <span className='font-medium text-foreground'>
                            {actorName(latest.accepted_by, 'un usuario')}
                          </span>
                          {latest.accepted_at &&
                            ` el ${formatDate(latest.accepted_at)}`}
                          .
                        </span>
                      </p>
                    )}

                    {latest.status === 'rejected' && (
                      <div className='flex flex-col gap-1'>
                        <p className='flex items-center gap-1.5 py-0.5 text-[13px] text-muted-foreground'>
                          <IconUserX className='h-3.5 w-3.5 shrink-0' />
                          <span>
                            Rechazado por{' '}
                            <span className='font-medium text-foreground'>
                              {actorName(latest.rejected_by, 'un usuario')}
                            </span>
                            {latest.rejected_at &&
                              ` el ${formatDate(latest.rejected_at)}`}
                            . Se puede subir una nueva versión.
                          </span>
                        </p>
                        {latest.rejected_reason && (
                          <p className='text-xs text-muted-foreground whitespace-pre-wrap pl-[22px]'>
                            Motivo:
                            <span className='font-medium text-foreground pl-1'>
                              «{latest.rejected_reason}»
                            </span>
                          </p>
                        )}
                      </div>
                    )}

                    <div>
                      {previous.length > 0 && (
                        <VersionHistoryDialog
                          documentName={item.name}
                          versions={previous}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className='text-center py-3 text-sm text-muted-foreground border border-dashed rounded-xl'>
                    Todavía no se cargó ningún documento.
                  </div>
                )}

                <div className='flex flex-col items-center md:items-end'>
                  <div className='flex items-center gap-2'>
                    {renderUploadAction && renderUploadAction({ item })}
                    {renderReviewActions &&
                      latest &&
                      renderReviewActions({ item, version: latest })}
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};
